import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import styled from "styled-components";
import emailjs from "@emailjs/browser";
import { AiFillCheckCircle } from "react-icons/ai";

const Contact = () => {
  const [success, setSuccess] = useState(false);

  const EmailSendConfirmation = () => {
    return (
      <StyledEmail
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        exit={{ scale: 0, opacity: 0 }}
      >
        <AiFillCheckCircle color="var(--vibrant)" size={"1.4rem"} />
        <span>Message sent successfully</span>
      </StyledEmail>
    );
  };

  const StyledEmail = styled(motion.div)`
    position: fixed;
    height: auto;
    border: none;
    padding: 1rem 1.25rem;
    border-radius: 12px;
    left: 50%;
    transform: translateX(-50%);
    bottom: 10%;
    background-color: var(--secondary-background);
    z-index: 999;
    display: flex;
    align-items: center;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(10px);
    span {
      margin-left: 0.5rem;
      color: var(--primary-text);
      font-weight: 500;
      letter-spacing: 0.05em;
    }
  `;

  const sendEmail = (e) => {
    e.preventDefault();

    emailjs
      .sendForm(
        "gmail_service",
        "portfolio_email",
        e.target,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )
      .then(
        (result) => {
          result.text === "OK" && setSuccess(true);
          setTimeout(() => {
            setSuccess(false);
          }, 2000);
        },
        (error) => {
          console.log(error.text);
        }
      );
    e.target.reset();
  };

  return (
    <>
      <AnimatePresence>{success && <EmailSendConfirmation />}</AnimatePresence>
      <StyledContact>
        <motion.div 
          className="contact-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="contact-wrapper">
            <motion.div 
              className="page-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h1>CONTACT</h1>
              <motion.div 
                className="title-bar"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </motion.div>
            
            <div className="contact-form">
              <motion.p 
                className="contact-message"
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                Have a question or want to work together? Drop me a message.
              </motion.p>
              
              <motion.form 
                onSubmit={sendEmail}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <div className="form-item">
                  <input type="text" name="name" required placeholder="Name" />
                </div>
                <div className="form-item">
                  <input type="email" name="email" required placeholder="Email" />
                </div>
                <div className="form-item">
                  <textarea 
                    name="message" 
                    required 
                    rows={4} 
                    placeholder="Message"
                  />
                </div>
                <button type="submit" className="submit-button">
                  <span>SEND</span>
                </button>
              </motion.form>
            </div>
          </div>
        </motion.div>
      </StyledContact>
    </>
  );
};

const StyledContact = styled(motion.div)`
  position: relative;
  /* min-height: calc(100vh - 80px); Adjusted to take full height minus footer height */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
  
  .contact-container {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .contact-wrapper {
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    padding: 3rem;
    width: 90%;
    height: 100%;
    
    .page-title {
      margin-bottom: 3rem;
      text-align: center;
      
      h1 {
        font-size: 2.5rem;
        font-weight: 800;
        letter-spacing: 0.15em;
        color: var(--heading-text);
        margin-bottom: 0.75rem;
      }
      
      .title-bar {
        height: 3px;
        width: 80px;
        margin: 0 auto;
        background: linear-gradient(
          90deg,
          var(--vibrant, #4a5bef) 0%,
          var(--accent-color, #61DAFB) 100%
        );
        border-radius: 2px;
        transform-origin: center;
      }
    }
    
    .contact-message {
      font-size: 1rem;
      line-height: 1.6;
      color: var(--secondary-text);
      margin-bottom: 2rem;
      letter-spacing: 0.05em;
      text-align: center;
      font-weight: 400;
    }
    
    .contact-form {
      width: 100%;
      max-width: 500px;
      margin: 0 auto;
      
      .form-item {
        margin-bottom: 1.5rem;
      }
      
      input[type="text"],
      input[type="email"],
      textarea {
        width: 100%;
        padding: 0.9rem;
        font-size: 0.9rem;
        border: 1px solid rgba(var(--border-color-rgb), 0.3);
        border-radius: 8px;
        background-color: rgba(var(--primary-background-rgb), 0.3);
        color: var(--primary-text);
        transition: all 0.3s ease;
        font-family: inherit;
        letter-spacing: 0.05em;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
        
        &:focus {
          outline: none;
          border-color: var(--vibrant);
          background-color: rgba(var(--primary-background-rgb), 0.5);
          box-shadow: 0 0 0 3px rgba(var(--vibrant-rgb), 0.15);
        }
        
        &::placeholder {
          color: rgba(var(--secondary-text-rgb), 0.6);
          font-weight: 400;
          letter-spacing: 0.05em;
        }
      }
      
      textarea {
        resize: none;
        min-height: 120px;
      }
      
      .submit-button {
        margin-top: 1rem;
        padding: 1rem 2rem;
        background: linear-gradient(
          90deg, 
          var(--vibrant, #4a5bef) 0%, 
          rgba(var(--vibrant-rgb), 0.8) 100%
        );
        color: white;
        font-size: 0.9rem;
        font-weight: 600;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.3s ease;
        width: auto;
        min-width: 140px;
        display: flex;
        justify-content: center;
        align-items: center;
        letter-spacing: 0.15em;
        box-shadow: 0 4px 12px rgba(var(--vibrant-rgb), 0.25);
        
        span {
          position: relative;
        }
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(var(--vibrant-rgb), 0.3);
        }

        &:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(var(--vibrant-rgb), 0.2);
        }
      }
    }
  }
  
  @media only screen and (max-width: 768px) {
    padding: 2rem 1rem;
    
    .contact-wrapper {
      width: 100%;
      padding: 2rem 1.5rem;
      
      .page-title {
        h1 {
          font-size: 2rem;
        }
      }
      
      .contact-message {
        font-size: 0.9rem;
      }
    }
  }
`;

export default Contact;
